export const colorIds = [
  'brand',
  'brandDark',
  'brandLight',
  'brandTint',
  'brandSecondary',
  'brandSecondaryLight',
  'brandSecondaryTint',
  'grey',
  'greyLight',
  'greyLighter',
  'greyBackground',
  'greyHover',
  'greyBorder',
  'greyText',
  'greyMedium',
  'textPrimary',
  'white',
  'highlight',
  'info',
  'infoLight',
  'infoLighter',
  'danger',
  'dangerLight',
  'dangerLighter',
  'dangerAccent',
  'success',
  'successLight',
  'successLighter',
  'successAccent',
  'successAlert',
  'secondary',
  'secondaryLight',
  'secondaryLighter',
  'warn',
  'warnLight',
  'warnLighter',
] as const
type colorId = (typeof colorIds)[number]
const colors: Record<colorId, string> = {
  brand: '#003057',
  brandDark: '#00253B',
  brandLight: '#277FB2',
  brandTint: 'rgba(0, 48, 87, 0.3)',
  brandSecondary: '#F89822',
  brandSecondaryLight: '#F7D084',
  brandSecondaryTint: 'rgba(248, 152, 34, 0.3)',
  grey: '#aaa',
  greyLight: '#ccc',
  greyLighter: '#eee',
  greyBackground: '#F8F8F9',
  greyHover: '#F2F2F2',
  greyBorder: '#E0E0E0',
  greyText: '#4A4A4A',
  greyMedium: '#70787D',
  textPrimary: '#262626',
  white: '#fff',
  highlight: '#FFEEBA',
  info: '#055160',
  infoLight: '#b6effb',
  infoLighter: '#cff4fc',
  danger: '#842029',
  dangerLight: '#f5c2c7',
  dangerLighter: '#f8d7da',
  dangerAccent: '#B2023B',
  success: '#0f5132',
  successLight: '#badbcc',
  successLighter: '#d1e7dd',
  successAccent: '#01A07E',
  successAlert: '#439313',
  secondary: '#41464b',
  secondaryLight: '#ded6d8',
  secondaryLighter: '#e2e3e5',
  warn: '#664d03',
  warnLight: '#ffecb5',
  warnLighter: '#fff3cd',
}

export default colors

// Material Design 3 color roles, grouped as main/on/container/onContainer
// quads (M3 calls these primary/onPrimary/primaryContainer/onPrimaryContainer,
// etc.) — grouped here instead of flattened so role-aware component work
// (tonal buttons, chips, MUI's palette slots) has a clear shape to consume.
// Every value here references the constants above; this file has one source
// of truth for each color, not two.
//
// Notes on fidelity to the M3 spec:
// - `container` values reuse this app's existing "Tint"/"Light" tokens,
//   several of which are semi-transparent overlays rather than solid M3-style
//   pastel fills. Good enough to unlock role-based theming now; swapping in
//   solid computed tints is a follow-up if exact M3 container fills matter.
// - `tertiary` has no dedicated brand color today, so it borrows the info/teal
//   accent (already used for links and subtitles) as the closest existing hue.
// - `warning.main` (`colors.warn`) is a dark, muted olive — it was designed as
//   Bootstrap-style alert *text*, not a vivid fill. It's the only "warning"
//   color this app has, so it's used as-is rather than inventing a new one.
export const roles = {
  primary: {
    main: colors.brand,
    on: colors.white,
    container: colors.brandTint,
    onContainer: colors.brand,
  },
  secondary: {
    main: colors.brandSecondary,
    on: colors.brand,
    container: colors.brandSecondaryTint,
    onContainer: colors.brand,
  },
  tertiary: {
    main: colors.brandLight,
    on: colors.white,
    container: colors.infoLighter,
    onContainer: colors.info,
  },
  error: {
    main: colors.dangerAccent,
    on: colors.white,
    container: colors.dangerLighter,
    onContainer: colors.danger,
  },
  success: {
    // `successAlert` (#439313) is only 3.87:1 against white — fails WCAG AA's
    // 4.5:1 for normal text, which matters because `main` is what a MUI
    // `color="success"` text-variant Button renders directly as its label
    // color on a white page (e.g. components/cff/upload-boundaries/index.tsx),
    // not just a fill with `on` as contrastText. `success` (#0f5132) is
    // ~9.4:1 against white, comfortably clearing AA, and is already used
    // as `onContainer` here, so it's already vetted for text legibility.
    main: colors.success,
    on: colors.white,
    container: colors.successLighter,
    onContainer: colors.success,
  },
  warning: {
    main: colors.warn,
    on: colors.white,
    container: colors.warnLighter,
    onContainer: colors.warn,
  },
  neutral: {
    main: colors.textPrimary,
    on: colors.white,
    container: colors.greyBackground,
    onContainer: colors.textPrimary,
  },
} as const

// M3's state-layer opacities (m3.material.io/foundations/interaction/states)
// — the consistent overlay strength interactive elements use for hover/
// focus/press/drag feedback, instead of every component picking its own
// darken/lighten/alpha amount. "selected" isn't a distinct M3 token; it
// shares the pressed value, since M3 doesn't define one separately.
export const STATE_LAYER_OPACITY = {
  hover: 0.08,
  focus: 0.1,
  pressed: 0.1,
  selected: 0.1,
  dragged: 0.16,
} as const

// M3's corner-radius scale (m3.material.io/styles/shape/corner-radius-scale)
// — a 7-step system from square to fully round, assigned by component role
// rather than picked per-component. The app has ~60 one-off borderRadius
// values scattered across content boxes (map popups, legends, toasts) that
// this doesn't touch — those are minor per-component styling, not part of
// the shared chrome (buttons, dialogs, inputs, nav) this scale is for.
export const SHAPE_SCALE = {
  none: '0px',
  extraSmall: '4px',
  small: '8px',
  medium: '12px',
  large: '16px',
  largeIncreased: '20px',
  extraLarge: '32px',
  full: '9999px',
} as const
